/**
 * AdminGridDashboard
 *
 * Drag-and-drop, resizable dashboard grid.
 * Layout auto-saves to localStorage on every change.
 * Panel visibility toggled via the "Panels" picker (also auto-saved).
 */

import React, { useRef, useState, useEffect, useContext } from 'react';
import { UserContext } from '../../../contexts/UserContext';
import { canOpenScreen } from '../../daily-task-coordination/utils/screenAccess';
import { Responsive } from 'react-grid-layout';
import { GripVertical, LayoutGrid, Eye, EyeOff } from 'lucide-react';
import MiniResourceTimeline from './MiniResourceTimeline';
import WarehouseSummaryPanel from './WarehouseSummaryPanel';
import WarehouseLowStockPanel from './WarehouseLowStockPanel';
import WarehouseMovementsPanel from './WarehouseMovementsPanel';
import WarehouseTopItemsPanel from './WarehouseTopItemsPanel';
import EquipmentSummaryPanel from './EquipmentSummaryPanel';
import EquipmentByTypePanel from './EquipmentByTypePanel';
import TimesheetSummaryPanel from './TimesheetSummaryPanel';
import TimesheetPendingPanel from './TimesheetPendingPanel';
import WarehouseAssignmentsPanel from './WarehouseAssignmentsPanel';
import BirthdaysPanel from './BirthdaysPanel';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

// Measure container width so Responsive fills its parent
const useContainerWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(1200);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.getBoundingClientRect().width);
    const ro = new ResizeObserver(entries => setWidth(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, width };
};

const PANELS = [
  { id: 'kpi-bento',          label: 'KPI Overview' },
  { id: 'chart-certs',        label: 'Certificate Expiry' },
  { id: 'chart-permits',      label: 'Work Permits Chart' },
  { id: 'activity',           label: 'Recent Activity' },
  { id: 'resource',           label: 'Resource Timeline' },
  { id: 'wh-summary',         label: 'Warehouse Overview' },
  { id: 'wh-low-stock',       label: 'Low Stock Alert' },
  { id: 'wh-movements',       label: 'Stock Movements' },
  { id: 'wh-top-items',       label: 'Top Used Items' },
  { id: 'eq-summary',         label: 'Equipment Overview' },
  { id: 'eq-by-type',         label: 'Equipment by Type' },
  // Read from Timesheets Management's endpoints — offered only to a role that opens it
  // (the API refuses the rest since 2026-10-27, which left an error in the panel).
  { id: 'ts-summary',         label: 'Timesheet Summary',  screen: 'timesheets-management' },
  { id: 'ts-pending',         label: 'Pending Timesheets', screen: 'timesheets-management' },
  { id: 'wh-assignments',     label: 'Assignments & Returns' },
  { id: 'birthdays',          label: 'Upcoming Birthdays' },
];

const panelsFor = (user) => PANELS.filter(p => !p.screen || canOpenScreen(user, p.screen));

const DragHandle = () => (
  <div
    className="drag-handle flex items-center justify-center h-5 bg-steel-100 hover:bg-steel-200 cursor-grab active:cursor-grabbing transition-colors flex-shrink-0 rounded-t-xl"
    style={{ touchAction: 'none' }}
  >
    <GripVertical size={14} className="text-slate-300 rotate-90" />
  </div>
);

const GridPanel = ({ children }) => (
  <div className="flex flex-col h-full overflow-hidden rounded-xl shadow-sm">
    <DragHandle />
    <div className="flex-1 min-h-0 overflow-auto">
      {children}
    </div>
  </div>
);

export const PanelPicker = ({ hiddenPanels, onToggle }) => {
  const { user } = useContext(UserContext) || {};
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-brand-600 hover:bg-brand-50 px-3 py-1.5 rounded-lg border border-steel-200 bg-white transition-colors"
      >
        <LayoutGrid size={13} />
        Panels
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-9 z-20 bg-white border border-steel-200 rounded-xl shadow-lg p-2 min-w-[180px]">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide px-2 py-1 mb-1">
              Toggle panels
            </p>
            {panelsFor(user).map(p => {
              const visible = !hiddenPanels.includes(p.id);
              return (
                <button
                  key={p.id}
                  onClick={() => onToggle(p.id)}
                  className="flex items-center gap-2 w-full px-2 py-1.5 rounded-lg hover:bg-steel-50 text-sm text-left transition-colors"
                >
                  {visible
                    ? <Eye size={14} className="text-brand-500 flex-shrink-0" />
                    : <EyeOff size={14} className="text-slate-300 flex-shrink-0" />
                  }
                  <span className={visible ? 'text-slate-700' : 'text-slate-400'}>{p.label}</span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const AdminGridDashboard = ({ kpiBento, certChart, permitsChart, activityTimeline, layouts, onLayoutChange, hiddenPanels }) => {
  const { ref: containerRef, width } = useContainerWidth();
  const { user } = useContext(UserContext) || {};

  const panelContent = {
    'kpi-bento':     kpiBento,
    'chart-certs':   certChart,
    'chart-permits': permitsChart,
    'activity':      activityTimeline,
    'resource':      <MiniResourceTimeline />,
    'wh-summary':    <WarehouseSummaryPanel />,
    'wh-low-stock':  <WarehouseLowStockPanel />,
    'wh-movements':  <WarehouseMovementsPanel />,
    'wh-top-items':  <WarehouseTopItemsPanel />,
    'eq-summary':    <EquipmentSummaryPanel />,
    'eq-by-type':    <EquipmentByTypePanel />,
    'ts-summary':    <TimesheetSummaryPanel />,
    'ts-pending':    <TimesheetPendingPanel />,
    'wh-assignments':<WarehouseAssignmentsPanel />,
    'birthdays':     <BirthdaysPanel />,
  };

  const visibleIds = panelsFor(user).map(p => p.id).filter(id => !hiddenPanels.includes(id));

  return (
    <div className="relative">
      <div ref={containerRef}>
        <Responsive
          width={width}
          layouts={layouts}
          onLayoutChange={onLayoutChange}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
          rowHeight={120}
          margin={[16, 16]}
          draggableHandle=".drag-handle"
          resizeHandles={['se']}
        >
          {visibleIds.map(id => (
            <div key={id}>
              <GridPanel>{panelContent[id]}</GridPanel>
            </div>
          ))}
        </Responsive>
      </div>
    </div>
  );
};

export default AdminGridDashboard;
